import type { Client, Health, Links } from '@ops/contracts'
import { CLIENT_STATE_LABELS } from '@ops/contracts'
import type { ReactNode } from 'react'

import { formatFechaCorta, formatMetricValue, plural } from '../../lib/format'
import { useVincular } from '../../lib/vincular'
import {
  DataTable,
  ExternalLink,
  Panel,
  ProgressBar,
  RecordChip,
  StatusPill,
  type Column,
} from '../../ui'
import { Vacio } from '../comunes'
import { claveEntidad, type Cobertura } from './frentes'
import { severidadDeEspera } from './AgingChart'

/**
 * Las tablas de cuentas del Overview de ops y comercial.
 *
 * **Ninguna variante tiene columna de plata, y no es configurable.** `Client.mrr`
 * llega en `null` para estos roles porque el servidor lo recorta, pero eso no es
 * "sin dato": es "no tenés permiso". Una columna de MRR con guiones le contaría
 * a ops que existe un número que no puede ver, así que la columna no existe.
 *
 * La densidad por severidad hace el trabajo que en el sheet hacía filtrar a
 * mano: la fila que espera 80 días respira y tiene fondo, la que está bien va
 * compacta y en gris.
 */
export type VarianteClientes = 'cola' | 'brecha' | 'riesgo' | 'expansion'

export interface ClientesPanelProps {
  variante: VarianteClientes
  clientes: readonly Client[]
  links: Links
  /** Seguimientos que ya cubren cada cuenta, indexados por entidad. */
  cobertura: Map<string, Cobertura>
  onCrearSeguimiento: (cliente: Client) => void
  titulo: string
  subtitulo?: ReactNode
  footer?: ReactNode
  /** Qué decir cuando la lista está vacía. Una oración, nunca "sin datos". */
  vacio: string
  /**
   * A qué sistema de origen linkea el nombre. Ops abre la ficha de la cuenta en
   * la plataforma; comercial abre su lead en Close. Stripe no aparece nunca en
   * estas tablas: es el sistema de la plata.
   */
  sistema: 'plataforma' | 'close'
}

/** Umbrales de espera de cada variante, en días. */
const UMBRALES: Record<VarianteClientes, { critico: number; atencion: number }> = {
  cola: { critico: 60, atencion: 30 },
  riesgo: { critico: 30, atencion: 14 },
  expansion: { critico: 120, atencion: 60 },
  brecha: { critico: 60, atencion: 30 },
}

/** Activación baja = riesgo: plata contratada que no está corriendo. */
export function severidadDeActivacion(pctActivado: number): Health {
  if (pctActivado < 50) return 'critical'
  if (pctActivado < 80) return 'warning'
  return 'ok'
}

export function severidadDeFila(
  variante: VarianteClientes,
  cliente: Client,
): Health {
  if (variante === 'brecha') {
    if (cliente.activacion === null) return 'unknown'
    return severidadDeActivacion(cliente.activacion.pctActivado)
  }
  const u = UMBRALES[variante]
  return severidadDeEspera(cliente.diasEnEstado, u.critico, u.atencion)
}

const ROTULO_DIAS: Record<VarianteClientes, string> = {
  cola: 'Días esperando',
  riesgo: 'Días en retención',
  expansion: 'Días en expansión',
  brecha: 'Días en estado',
}

export function ClientesPanel({
  variante,
  clientes,
  links,
  cobertura,
  onCrearSeguimiento,
  titulo,
  subtitulo,
  footer,
  vacio,
  sistema,
}: ClientesPanelProps) {
  const vincular = useVincular()

  if (clientes.length === 0) {
    return (
      <Panel titulo={titulo} subtitulo={subtitulo} padding="sm">
        <Vacio icono={null} mensaje={vacio} />
      </Panel>
    )
  }

  const nombre: Column<Client> = {
    key: 'nombre',
    header: 'Cuenta',
    rowHeader: true,
    width: '230px',
    sortValue: (c) => c.nombre,
    filterValue: (c) => `${c.nombre} ${c.vertical}`,
    render: (c) => (
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate">{c.nombre}</span>
        <ExternalLink
          link={links.clientes[c.id]?.[sistema] ?? null}
          variante="chip"
          sistemaFaltante={sistema === 'close' ? 'Close' : 'la plataforma'}
          onVincular={() =>
            vincular(
              sistema === 'close'
                ? `el lead en Close de ${c.nombre}`
                : `la ficha de ${c.nombre} en la plataforma`,
            )
          }
        >
          {sistema === 'close' ? 'Lead en Close' : 'Ficha en la plataforma'}
        </ExternalLink>
      </span>
    ),
  }

  const vertical: Column<Client> = {
    key: 'vertical',
    header: 'Vertical',
    width: '120px',
    sortValue: (c) => c.vertical,
    render: (c) => c.vertical,
  }

  const estado: Column<Client> = {
    key: 'estado',
    header: 'Estado',
    width: '160px',
    sortValue: (c) => CLIENT_STATE_LABELS[c.estado],
    render: (c) => (
      <StatusPill severidad={severidadDeFila(variante, c)} size="sm">
        {CLIENT_STATE_LABELS[c.estado]}
      </StatusPill>
    ),
  }

  const dias: Column<Client> = {
    key: 'dias',
    header: ROTULO_DIAS[variante],
    align: 'right',
    width: '110px',
    sortValue: (c) => c.diasEnEstado,
    render: (c) => formatMetricValue(c.diasEnEstado, 'count'),
  }

  const activacion: Column<Client> = {
    key: 'activacion',
    header: 'Activación',
    width: '210px',
    sortValue: (c) => c.activacion?.pctActivado ?? null,
    render: (c) =>
      c.activacion === null ? (
        <span className="text-micro text-muted-strong">
          Sin medición de activación
        </span>
      ) : (
        <span className="flex min-w-0 flex-col gap-1">
          <ProgressBar
            pct={c.activacion.pctActivado}
            label={`Activación de ${c.nombre}`}
            severidad={severidadDeActivacion(c.activacion.pctActivado)}
            etiqueta={formatMetricValue(c.activacion.pctActivado, 'percent')}
            size="sm"
          />
          {/* El % nunca va solo: al lado va la base con la que se calcula. */}
          <span className="tabular text-micro text-muted-strong">
            {formatMetricValue(c.activacion.instalados, 'count')} de{' '}
            {formatMetricValue(c.activacion.contratados, 'count')} instalados ·
            faltan {formatMetricValue(c.activacion.brecha, 'count')}
          </span>
        </span>
      ),
  }

  const contacto: Column<Client> = {
    key: 'contacto',
    header: 'Último contacto',
    width: '120px',
    sortValue: (c) => c.ultimoContacto,
    render: (c) =>
      c.ultimoContacto === null ? (
        <span className="text-micro text-muted-strong">Nunca registrado</span>
      ) : (
        <span className="tabular">{formatFechaCorta(c.ultimoContacto)}</span>
      ),
  }

  const dueno: Column<Client> = {
    key: 'dueno',
    header: 'Dueño',
    width: '140px',
    sortValue: (c) => c.owner?.nombre ?? null,
    render: (c) =>
      c.owner === null ? (
        <span className="text-micro text-muted-strong">Sin dueño</span>
      ) : (
        c.owner.nombre
      ),
  }

  const seguimiento: Column<Client> = {
    key: 'seguimiento',
    header: 'Seguimiento',
    width: '230px',
    render: (c) => {
      const cubierto = cobertura.get(claveEntidad('client', c.id))
      return cubierto === undefined ? (
        <RecordChip
          link={null}
          rotuloVacio="Sin seguimiento"
          onCrear={() => onCrearSeguimiento(c)}
          textoCrear="Asignar"
        />
      ) : (
        <RecordChip
          link={cubierto.link}
          identificador={cubierto.followup.planeIdentificador}
          estado={cubierto.followup.estado}
          estadoGroup={cubierto.followup.estadoGroup}
          rotulo="Ya tiene:"
        />
      )
    },
  }

  const columnas: Record<VarianteClientes, ReadonlyArray<Column<Client>>> = {
    cola: [nombre, vertical, dias, dueno, seguimiento],
    brecha: [nombre, activacion, dueno, seguimiento],
    riesgo: [nombre, vertical, dias, contacto, dueno, seguimiento],
    expansion: [nombre, vertical, estado, dias, dueno, seguimiento],
  }

  return (
    <Panel titulo={titulo} subtitulo={subtitulo} padding="none" footer={footer}>
      <DataTable
        columns={columnas[variante]}
        rows={clientes}
        rowKey={(c) => c.id}
        rowSeverity={(c) => severidadDeFila(variante, c)}
        densidadPorSeveridad
        ordenInicial={
          variante === 'brecha'
            ? { key: 'activacion', dir: 'asc' }
            : { key: 'dias', dir: 'desc' }
        }
        filtroPlaceholder={
          clientes.length > 8 ? 'Nombre o vertical' : undefined
        }
        caption={`${formatMetricValue(clientes.length, 'count')} ${plural(clientes.length, 'cuenta', 'cuentas')} · orden por gravedad, la fila crítica respira y la sana va compacta`}
        emptyMessage="Ninguna cuenta coincide con el filtro"
      />
    </Panel>
  )
}
